var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AlertIDType } from '../../../const/alertsConfig';
import buttonAnalytics from '../../../helpers/buttonAnalytics';
import { onTurnOffPreference, onUpdatePreference, validateAndFormatAttributeValue } from '../../../redux/alertsForm/actions';
import { getHasAcknowledgeTermsConditions, getPurchaseExceedsDataErrors, getPurchaseExceedsIsDirty, getPurchaseExceedsKey } from '../../../redux/selectors';
import { colors } from '../../../theme';
import { useButtonAnalytics } from '../../customHooks';
import { getAprrovedTransactionDataMessage, getDataAttributes, getDataMessage, getSelectedTransactionOption, isrelevantlyDirty } from './helpers';
import { AlertBodyFormButtons, AlertButtonWrapper, AlertsSpinnerLoader, CustomButton } from './subcomponents';
export var ContentOrSpinner = function (_a) {
    var translation = _a.translation, isLoading = _a.isLoading, currentButtonClicked = _a.currentButtonClicked;
    return isLoading && currentButtonClicked === translation ? (_jsx(AlertsSpinnerLoader, { "data-test": "SpinnerLoader", color: colors.white, size: "2x" })) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    _jsx(_Fragment, { children: translation }));
};
var AlertButtons = function (_a) {
    var _b, _c, _d, _e;
    var currentAlertData = _a.currentAlertData, alertFormData = _a.alertFormData, hasUnchangeableItems = _a.hasUnchangeableItems, focusRef = _a.focusRef, onSaveOrTurnOnButtonClick = _a.onSaveOrTurnOnButtonClick;
    var t = useTranslation().t;
    var TURN_ON_I18N = t('buttonLabels.turnOn');
    var TURN_OFF_I18N = t('buttonLabels.turnOff');
    var SAVE_I18N = t('buttonLabels.save');
    var hasSmsConsentSelector = useMemo(function () { return getHasAcknowledgeTermsConditions; }, []);
    var hasSmsConsent = useSelector(hasSmsConsentSelector, shallowEqual);
    // disable alert button logic for approved alerts
    var isPurchaseExceedAlertDirty = useSelector(getPurchaseExceedsIsDirty, shallowEqual);
    var dataErrorsForPurchaseExceedsAlert = useSelector(getPurchaseExceedsDataErrors, shallowEqual);
    var purchaseExceedsThresholdAmount = useSelector(getPurchaseExceedsKey, shallowEqual);
    var hasPurchaseExceedsValue = ((_c = (_b = alertFormData === null || alertFormData === void 0 ? void 0 : alertFormData.attributes) === null || _b === void 0 ? void 0 : _b.approvedTransactionType) === null || _c === void 0 ? void 0 : _c.value) ===
        AlertIDType.PURCHASE_EXCEEDS;
    var approvedTransactionAttributeValue = (_e = (_d = alertFormData === null || alertFormData === void 0 ? void 0 : alertFormData.attributes) === null || _d === void 0 ? void 0 : _d.approvedTransactionType) === null || _e === void 0 ? void 0 : _e.value;
    // disable alert button logic for approved alerts ends
    var phoneNumberUnavailable = alertFormData.dataErrors.some(function (error) { return error.key === 'phoneWarning'; });
    var alertId = currentAlertData.id, isAlertOn = currentAlertData.isAlertOn;
    var isDirty = alertFormData.isDirty, isLoading = alertFormData.isLoading, delivery = alertFormData.delivery, dataErrors = alertFormData.dataErrors;
    var hasSelectedText = delivery.text.enrolled;
    var dispatch = useDispatch();
    var _f = useState(''), currentButtonClicked = _f[0], setCurrentButtonClicked = _f[1];
    var onTurnOff = useCallback(function () {
        dispatch(onTurnOffPreference(alertId));
        setCurrentButtonClicked(TURN_OFF_I18N.toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, alertId]);
    // added to update redux store for purchase exceeds alert - part of approved transaction
    var updatePurchaseExceedsDetails = useCallback(function () {
        if (alertId === AlertIDType.APPROVED_TRANSACTIONS &&
            hasPurchaseExceedsValue) {
            dispatch(validateAndFormatAttributeValue(AlertIDType.PURCHASE_EXCEEDS, purchaseExceedsThresholdAmount));
        }
    }, [
        alertId,
        dispatch,
        hasPurchaseExceedsValue,
        purchaseExceedsThresholdAmount
    ]);
    // ends - added to update redux store for purchase exceeds alert - part of approved transaction
    var onSave = useCallback(function () {
        updatePurchaseExceedsDetails();
        dispatch(onUpdatePreference(alertId));
        setCurrentButtonClicked(SAVE_I18N.toString());
        onSaveOrTurnOnButtonClick();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        alertId,
        updatePurchaseExceedsDetails,
        onSaveOrTurnOnButtonClick
    ]);
    var onTurnOn = useCallback(function () {
        updatePurchaseExceedsDetails();
        dispatch(onUpdatePreference(alertId));
        setCurrentButtonClicked(TURN_ON_I18N.toString());
        onSaveOrTurnOnButtonClick();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        alertId,
        updatePurchaseExceedsDetails,
        onSaveOrTurnOnButtonClick
    ]);
    var isApprovedTransactionAlert = alertId === AlertIDType.APPROVED_TRANSACTIONS;
    var _g = useButtonAnalytics(currentAlertData, alertFormData), dataFunction = _g.dataFunction, dataSubFunction = _g.dataSubFunction, dataName = _g.dataName, dataReason = _g.dataReason, dataChannels = _g.dataChannels, dataMessage = _g.dataMessage, dataType = _g.dataType, dataAnalytics = _g.dataAnalytics;
    var dataAttributesWithConsent = {
        function: dataFunction,
        subfunction: dataSubFunction,
        name: dataName
    };
    var defaultDataAttributes = {
        function: '',
        subfunction: '',
        name: ''
    };
    var dataAttributes = getDataAttributes(hasSelectedText, hasSmsConsent, defaultDataAttributes, dataAttributesWithConsent);
    var dataMessageForApprovedTransaction = getAprrovedTransactionDataMessage(dataErrorsForPurchaseExceedsAlert, dataErrors);
    var message = getDataMessage(isApprovedTransactionAlert, hasPurchaseExceedsValue, dataMessageForApprovedTransaction, dataMessage);
    var transactionOption = getSelectedTransactionOption(alertId, approvedTransactionAttributeValue);
    var relevantlyDirty = isrelevantlyDirty(alertId, hasPurchaseExceedsValue, isPurchaseExceedAlertDirty, isDirty);
    return (_jsx(AlertButtonWrapper, { children: _jsx(AlertBodyFormButtons, __assign({ "data-test": "AlertBodyFormButtons", hasChangeableItems: true }, { children: !isAlertOn ? (_jsx(CustomButton, __assign({ "data-test": "CustomButton-on", "data-function": dataAttributes.function, "data-subfunction": dataAttributes.subfunction, "data-name": dataAttributes.name, "data-channels": dataChannels, "data-reason": "".concat(dataFunction, ":").concat(dataReason, ":turn on"), "data-message": message, "data-type": dataType, "data-transaction-option": transactionOption, "data-analytics": dataAnalytics, "data-enroll": buttonAnalytics(alertFormData), className: "ensightenalertevent", disabled: isLoading || phoneNumberUnavailable, buttonType: "primary", onClick: onTurnOn, ref: focusRef }, { children: _jsx(ContentOrSpinner, { translation: TURN_ON_I18N, isLoading: isLoading, currentButtonClicked: currentButtonClicked }) }))) : (_jsxs(_Fragment, { children: [!hasUnchangeableItems && (_jsx(CustomButton, __assign({ "data-test": "CustomButton-off", "data-function": dataFunction, "data-subfunction": dataSubFunction.replace('modify', 'unenroll'), "data-name": dataName, "data-reason": dataReason.replace('save', 'turn off'), "data-message": dataMessage, "data-type": dataType, "data-analytics": dataAnalytics, "data-enroll": buttonAnalytics(alertFormData), className: "ensightenalertevent", buttonType: "text", disabled: isLoading && currentButtonClicked === TURN_OFF_I18N.toString(), type: "button", onClick: onTurnOff }, { children: TURN_OFF_I18N }))), "\u00A0", _jsx(CustomButton, __assign({ "data-test": "CustomButton-save", "data-function": dataAttributes.function, "data-subfunction": dataAttributes.subfunction, "data-name": dataAttributes.name, "data-channels": dataChannels, "data-reason": "".concat(dataFunction, ":").concat(dataReason.replace('save', 'modify'), ":save"), "data-message": message, "data-transaction-option": transactionOption, "data-type": dataType, "data-analytics": dataAnalytics, "data-enroll": buttonAnalytics(alertFormData), className: "ensightenalertevent", disabled: !relevantlyDirty ||
                            (isLoading && currentButtonClicked === SAVE_I18N.toString()) ||
                            phoneNumberUnavailable, buttonType: "primary", onClick: onSave, ref: focusRef }, { children: _jsx(ContentOrSpinner, { translation: SAVE_I18N, currentButtonClicked: currentButtonClicked, isLoading: isLoading }) }))] })) })) }));
};
export default AlertButtons;
